import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router';
import {
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
  CheckIcon,
  ShieldCheckIcon,
  ChartBarSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import authService from '../../services/auth.service';
import { SignUpRequest, ProductLine } from '../../types/auth.types';
import { getAuthErrorMessage, getValidationErrors } from '../../utils/authErrorHandler';
import { usePricing } from '../../hooks/usePricing';
import AuthShell from './AuthShell';
import AuthField from './AuthField';
import AuthSubmitButton from './AuthSubmitButton';

interface ProductOption {
  id: ProductLine;
  name: string;
  tagline: string;
  price: string;
  features: string[];
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  recommended?: boolean;
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { formatPrice } = usePricing();

  const PRODUCT_OPTIONS: ProductOption[] = useMemo(
    () => [
      {
        id: 'comply',
        name: 'CallCov COMPLY',
        tagline: t('auth:productSelection.comply.tagline'),
        price: `${formatPrice('comply', 'starter')}/mes`,
        features: t('auth:productSelection.comply.features', { returnObjects: true }) as string[],
        icon: ShieldCheckIcon,
        recommended: true,
      },
      {
        id: 'quality',
        name: 'CallCov QUALITY',
        tagline: t('auth:productSelection.quality.tagline'),
        price: `${formatPrice('quality', 'starter')}/mes`,
        features: t('auth:productSelection.quality.features', { returnObjects: true }) as string[],
        icon: ChartBarSquareIcon,
      },
      {
        id: 'full',
        name: 'CallCov FULL',
        tagline: t('auth:productSelection.full.tagline'),
        price: `${formatPrice('full', 'starter')}/mes`,
        features: t('auth:productSelection.full.features', { returnObjects: true }) as string[],
        icon: SparklesIcon,
      },
    ],
    [formatPrice, t]
  );

  const initialProduct = (searchParams.get('product') as ProductLine) || 'full';
  const [step, setStep] = useState<'product' | 'details'>(
    searchParams.has('product') ? 'details' : 'product'
  );
  const [selectedProduct, setSelectedProduct] = useState<ProductLine>(initialProduct);
  const [formData, setFormData] = useState<SignUpRequest>({
    email: '',
    phoneNumber: '',
    password: '',
    productLine: initialProduct,
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = t('auth:signUp.emailInvalid');
    }

    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = t('auth:signUp.phoneInvalid');
    }

    if (formData.password.length < 8) {
      newErrors.password = t('auth:signUp.passwordTooShort');
    }
    if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = t('auth:signUp.passwordMissingUppercase');
    }
    if (!/[a-z]/.test(formData.password)) {
      newErrors.password = t('auth:signUp.passwordMissingLowercase');
    }
    if (!/\d/.test(formData.password)) {
      newErrors.password = t('auth:signUp.passwordMissingDigit');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      newErrors.password = t('auth:signUp.passwordMissingSpecial');
    }

    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = t('auth:signUp.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    if (!validateForm()) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      const result = await authService.signUp(formData);
      if (result.success) {
        navigate('/verify-email', {
          state: { email: formData.email, message: result.message },
        });
      }
    } catch (error: any) {
      const validationErrors = getValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        toast.error(t('auth:signUp.registrationFailed'));
      } else {
        const errorMessage = getAuthErrorMessage(error, t, 'signUp');
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return { score: 0, text: '', barClass: '' };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    const tiers = [
      { text: t('auth:signUp.veryWeak'), barClass: 'bg-red-500' },
      { text: t('auth:signUp.weak'), barClass: 'bg-orange-500' },
      { text: t('auth:signUp.fair'), barClass: 'bg-amber-500' },
      { text: t('auth:signUp.good'), barClass: 'bg-sky-500' },
      { text: t('auth:signUp.strong'), barClass: 'bg-emerald-500' },
    ];
    return { score, ...tiers[Math.min(score, 4)] };
  };

  const passwordStrength = getPasswordStrength();

  const handleProductSelect = (productId: ProductLine) => {
    setSelectedProduct(productId);
    setFormData((prev) => ({ ...prev, productLine: productId }));
  };

  // ---------- Step 1: product picker ----------
  if (step === 'product') {
    const selectedName =
      PRODUCT_OPTIONS.find((p) => p.id === selectedProduct)?.name || '';

    return (
      <AuthShell
        maxWidth="4xl"
        title={t('auth:signUp.chooseProduct')}
        subtitle={
          <>
            {t('auth:signUp.chooseProductSubtitle')}{' '}
            <Link
              to="/signin"
              className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {t('auth:signUp.subtitle')}
            </Link>
          </>
        }
      >
        <div className="grid gap-5 sm:grid-cols-3">
          {PRODUCT_OPTIONS.map((product) => {
            const Icon = product.icon;
            const isSelected = selectedProduct === product.id;
            return (
              <button
                key={product.id}
                type="button"
                onClick={() => handleProductSelect(product.id)}
                aria-pressed={isSelected}
                className={`relative text-left p-6 border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  isSelected
                    ? 'border-sky-500 bg-sky-500/[0.05]'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {product.recommended && (
                  <span className="absolute -top-3 left-6 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] bg-sky-600 text-white">
                    {t('auth:signUp.recommended')}
                  </span>
                )}
                <div className="flex items-center justify-between mb-5">
                  <Icon
                    className={`h-7 w-7 ${
                      isSelected ? 'text-sky-400' : 'text-white/60'
                    }`}
                  />
                  {isSelected && <CheckIcon className="h-5 w-5 text-sky-400" />}
                </div>
                <h3 className="text-lg font-medium text-white">{product.name}</h3>
                <p className="mt-1 text-sm text-white/60">{product.tagline}</p>
                <p className="mt-4 text-2xl font-light text-white">{product.price}</p>
                <ul className="mt-5 space-y-2.5">
                  {product.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-sm text-white/70"
                    >
                      <CheckIcon className="h-4 w-4 text-sky-400 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <div className="w-full sm:w-auto sm:min-w-[280px]">
            <AuthSubmitButton type="button" onClick={() => setStep('details')}>
              {t('auth:signUp.continueWithProduct', { product: selectedName })}
            </AuthSubmitButton>
          </div>
        </div>
      </AuthShell>
    );
  }

  // ---------- Step 2: account details ----------
  const selectedProductName =
    PRODUCT_OPTIONS.find((p) => p.id === selectedProduct)?.name || '';

  return (
    <AuthShell
      title={t('auth:signUp.title')}
      subtitle={
        <>
          {t('auth:signUp.subtitlePrefix')}{' '}
          <Link
            to="/signin"
            className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {t('auth:signUp.subtitle')}
          </Link>
        </>
      }
    >
      <div className="mb-8 flex items-center justify-center gap-3 text-sm">
        <span className="px-3 py-1 border border-sky-500/40 bg-sky-500/[0.06] text-sky-400">
          {selectedProductName}
        </span>
        <button
          type="button"
          onClick={() => setStep('product')}
          className="text-white/60 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          {t('auth:signUp.changeProduct')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthField
          id="email"
          name="email"
          type="email"
          label={t('auth:signUp.emailLabel')}
          value={formData.email}
          onChange={handleInputChange}
          placeholder={t('auth:signUp.emailPlaceholder')}
          error={errors.email}
          icon={EnvelopeIcon}
          required
          autoComplete="email"
        />

        <AuthField
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          label={t('auth:signUp.phoneLabel')}
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder={t('auth:signUp.phonePlaceholder')}
          error={errors.phoneNumber}
          icon={PhoneIcon}
          required
          autoComplete="tel"
        />

        <div>
          <AuthField
            id="password"
            name="password"
            type="password"
            label={t('auth:signUp.passwordLabel')}
            value={formData.password}
            onChange={handleInputChange}
            placeholder={t('auth:signUp.passwordPlaceholder')}
            error={errors.password}
            icon={LockClosedIcon}
            required
            autoComplete="new-password"
            helpText={t('auth:signUp.passwordRequirements')}
          />
          {formData.password && (
            <div className="mt-3">
              <div className="flex justify-between items-center text-xs mb-1.5">
                <span className="text-white/60">
                  {t('auth:signUp.passwordStrength')}
                </span>
                <span className="text-white/80">{passwordStrength.text}</span>
              </div>
              <div className="w-full bg-white/[0.05] h-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${passwordStrength.barClass}`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <AuthField
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          label={t('auth:signUp.confirmPasswordLabel')}
          value={confirmPassword}
          onChange={handleInputChange}
          placeholder={t('auth:signUp.passwordPlaceholder')}
          error={errors.confirmPassword}
          icon={LockClosedIcon}
          required
          autoComplete="new-password"
        />

        <label className="flex items-start gap-3 text-sm text-white/70">
          <input
            id="agree-terms"
            type="checkbox"
            required
            className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/[0.03] text-sky-500 focus:ring-2 focus:ring-sky-500/30 focus:ring-offset-0"
          />
          <span>
            {t('auth:signUp.agreeToTerms')}{' '}
            <Link
              to="/terms"
              className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none"
            >
              {t('auth:signUp.termsAndConditions')}
            </Link>{' '}
            {t('common:and')}{' '}
            <Link
              to="/privacy"
              className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none"
            >
              {t('auth:signUp.privacyPolicy')}
            </Link>
            .
          </span>
        </label>

        <AuthSubmitButton
          isLoading={isLoading}
          loadingLabel={t('auth:signUp.submittingButton')}
        >
          {t('auth:signUp.submitButton')}
        </AuthSubmitButton>
      </form>
    </AuthShell>
  );
};

export default SignUp;
